import { useEffect, useState } from 'react';
import { useDevicePref } from './devicePrefs.js';

/**
 * `unsupported` — no Screen Wake Lock API here (Firefox, older iOS).
 * `off` — nothing to hold awake right now, or the person switched it off.
 * `active` — the screen is being held on.
 * `denied` — the browser refused the lock; the screen dims as usual.
 */
export type WakeLockStatus = 'unsupported' | 'off' | 'active' | 'denied';

function wakeLockApi(): WakeLock | null {
  if (typeof navigator === 'undefined') return null;
  // Declared non-optional in lib.dom, absent in real browsers that predate it.
  const nav = navigator as Navigator & { wakeLock?: WakeLock };
  return nav.wakeLock ?? null;
}

export function wakeLockSupported(): boolean {
  return wakeLockApi() !== null;
}

/**
 * Holds the screen on while a session is running.
 *
 * This is the most mobile-specific problem the app has. Swiping through a
 * deck is not continuous input: you read a card, you talk about it, somebody
 * fetches drinks — and every phone in the room dims and locks after thirty
 * seconds of that. Picking the phone back up costs a lock screen, a passcode
 * and the place in the conversation.
 *
 * Two properties of the API shape the implementation. A lock is released
 * automatically the moment the tab stops being visible, and it is *not*
 * re-acquired when the tab comes back — so the visibility listener below is
 * not an optimisation, it is what makes the feature survive the first time
 * somebody answers a message. And `request()` rejects rather than resolving
 * to null when it is refused (battery saver, an unfocused tab), which is a
 * fact about the device and not an error worth showing anybody.
 */
export function useWakeLock(wanted: boolean): WakeLockStatus {
  const [enabled] = useDevicePref('keepAwake');
  const [status, setStatus] = useState<WakeLockStatus>(() =>
    wakeLockSupported() ? 'off' : 'unsupported',
  );

  useEffect(() => {
    const api = wakeLockApi();
    if (!api) {
      setStatus('unsupported');
      return;
    }
    if (!wanted || !enabled) {
      setStatus('off');
      return;
    }

    let cancelled = false;
    let sentinel: WakeLockSentinel | null = null;

    const acquire = async () => {
      if (cancelled || sentinel) return;
      try {
        const next = await api.request('screen');
        if (cancelled) {
          void next.release().catch(() => undefined);
          return;
        }
        sentinel = next;
        setStatus('active');
        // The browser drops the lock on its own when the tab is hidden; this
        // keeps our own bookkeeping honest when that happens.
        next.addEventListener('release', () => {
          if (sentinel === next) sentinel = null;
        });
      } catch {
        if (!cancelled) setStatus('denied');
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') void acquire();
    };

    void acquire();
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      const held = sentinel;
      sentinel = null;
      // A release can reject if the lock is already gone — nothing to do
      // about that, and nothing that should reach anybody.
      if (held) void held.release().catch(() => undefined);
    };
  }, [wanted, enabled]);

  return status;
}
